<template>
  <section class="ab-dropdown-input" :disabled="disabled">
    <input
      class="input"
      ref="input"
      type="text"
      :name="name"
      :disabled="disabled"
      :value="computedValue"
      @input="handleInput"
      @focus="handleFocus"
      @click="handleFocus"
      @blur="handleLeave">
    <span class="bar"></span>
    <label :class="inputValue ? 'active' : ''">{{ label }}</label>
    <span class="error-msg" hidden>{{ errorMessage }}</span>
    <div class="dropdown" v-show="showDropdown">
      <ul>
        <li v-for="(option, idx) in filteredResults" :key="idx">
          <label>
            <input
              type="radio"
              :value="option.id"
              :name="`d-${name}`"
              v-model="selected"
              @click="selectItem(option)">{{ option.label }}</label>
        </li>
      </ul>
      <div class="footer-li"><label @click="handleCreate"><strong>"{{ inputValue }}"</strong> (créer)</label></div>
    </div>
  </section>
</template>

<script>
  import Fuse from 'fuse.js';
  import { setTimeout } from 'timers';
  export default {
    name: "ab-dropdown-input",
    props: {
      options: {
        type: Array,
      },
      label: String,
      name: {
        type: String,
        default: ''
      },
      value: [ String, Number ],
      disabled: Boolean,
      errorMessage: String,
    },
    data() {
      return {
        inputValue: '',
        edited: false,
        selected: this.value,
        focused: false,
      }
    },
    created() {
      this.initFuse();
    },
    computed: {
      computedValue() {
        if (!this.options.length) return;
        const selectedItem = this.options.find(option => option.id == this.selected);
        return selectedItem && !this.edited ? selectedItem.label : this.inputValue;
      },
      filteredResults() {
        if (!this.fuse) return [];
        return this.inputValue ? this.fuse.search(this.inputValue) : [];
      },
      showDropdown() {
        if (this.focused && this.inputValue.length >= 2) return true;
        return false;
      }
    },
    watch: {
      /**
       * When v-model is changed:
       *   1. Set internal value.
       */
      value(value) {
        this.selected = value;
      },
      options(value) {
        this.initFuse();
      }
    },
    methods: {
      initFuse() {
        this.fuse = new Fuse(this.options, {
          matchAllTokens: true,
          findAllMatches: false,
          minMatchCharLength: 2,
          shouldSort: true,
          keys: [ 'label' ],
          threshold: 0.2,
        });
      },
      selectItem(option) {
        this.inputValue = option.label;
        this.$emit('input', option.id);
        this.$refs.input.focus();
        this.edited = false;
      },
      handleFocus(e) {
        this.focused = true;
        this.$emit('focus');
      },
      handleLeave(e) {
        this.$emit('blur');
        setTimeout(() => this.focused = false, 180);
      },
      handleInput(e) {
        this.inputValue = e.target.value;
        this.edited = true
      },
      handleCreate(e) {
        this.$emit('create:new', this.inputValue);
      },
      reset() {
        this.inputValue = '';
      }
    }
  }
</script>

<style scoped>
  /* MD input */
  /* INPUT ------------------------------- */
  .ab-dropdown-input{
    position: relative;
  }
  .input {
    color: var(--app-dark-grey);
    border: none;
    border-bottom: 2px solid var(--app-input-bar);
    font-size: 1.2rem;
    font-family: var(--app-font-regular);
    padding: 3px 0;
    display: block;
    width: 100%;
    background: transparent;
    box-sizing: border-box;
    text-align: inherit;
  }
  .input:focus { outline:none; }
  .input:disabled {
    color: #cecece;
  }
  /* LABEL ======================================= */
  .ab-dropdown-input > label {
    color: var(--app-dark-grey);
    font-weight: normal;
    position: absolute;
    pointer-events: none;
    top: 8px;
    will-change: transform;

    transition: 0.2s ease all; 
    -moz-transition: 0.2s ease all; 
    -webkit-transition: 0.2s ease all;
  }
  .ab-dropdown-input[disabled] label { color: #cecece; }
  .ab-dropdown-input.mandatory label::after {
    display: inline-block;
    margin-left: 0.1rem;
    line-height: 1rem;
    font-size: 1rem;
    color: var(--app-dark-grey);
    content: '*';
    vertical-align: middle;
  }
  /* active state */
  .ab-dropdown-input.mandatory input:focus ~ label::after, .ab-dropdown-input.mandatory .active::after {
    color: var(--app-red-color);
  }
  .input:focus ~ label, .input:valid ~ .ab-dropdown-input label, .ab-dropdown-input .active {
    font-size: 10px;
    color: var(--app-primary-color);

    transform: translateY(-20px);
  }
  /* BOTTOM BARS ================================= */
  .bar {
    position: relative;
    display: block;
    width: 100%;
  }
  .bar:before, .bar:after {
    background: var(--app-primary-color);

    content: '';
    height: 2px;
    width: 0;
    bottom: 0px;
    position: absolute;
    transition: 0.2s ease all;
    -moz-transition: 0.2s ease all;
    -webkit-transition: 0.2s ease all;
  }
  .bar:before {
    left: 50%;
  }
  .bar:after {
    right: 50%;
  }
  /* active state */
  .input:focus ~ .bar:before, .input:focus ~ .bar:after {
    width: 50%;
  }

  /* active state */
  .input:focus ~ .highlight {
    -webkit-animation:inputHighlighter 0.3s ease;
    -moz-animation:inputHighlighter 0.3s ease;
    animation:inputHighlighter 0.3s ease;
  }
  /* MD input */

  /* Icon-button */
  .icon-button {
    position: absolute;  
    display: inline-block;
    top: 0;
    right: 0;
  }
  .icon-button button {
    background: transparent;
    border: none;
    display: inline-block;
    cursor: pointer;
  }

  /* Dropdown */
  .dropdown {
    background-color: var(--app-select-dropdown);
    position: absolute;
    z-index: 10;
    box-shadow: var(--app-ctn-shadow);
    border-radius: 0 0 5px 5px;
    min-width: 100%;
  }

  .dropdown ul {
    list-style: none;
    font-size: 1rem;
    margin: 0;
    padding: 1rem 0;
    width: 100%;
    max-height: 20rem;
    overflow-y: auto;
  }
  .dropdown ul li {
    display: block;
    color: var(--app-dark-grey);
    font-weight: normal;
  }
  
  .dropdown ul li label,
  .footer-li label {
    display: block;
    padding: 0.3rem 1rem;
    cursor: pointer;
  }
  .dropdown ul li:hover,
  .footer-li:hover {
    background-color: var(--app-primary-color);
    color: var(--app-white-color);
  }
  .footer-li {
    font-size: 1rem;
    font-weight: normal;
    margin-bottom: 0.4rem;
  }

  .dropdown input[type=radio] { display: none; }
</style>